import { List, useTable } from '@refinedev/antd';
import { selectTenantPrompt } from '../../copy';
import { useDelete } from '@refinedev/core';
import { useNavigate, useSearchParams } from 'react-router';
import { Button, Empty, Tag, Typography } from 'antd';
import {
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { Can } from '../../ability/AbilityContext';
import type { DiscoveryQuestion } from './types';

export function typeLabel(q: DiscoveryQuestion): string {
  if (q.type === 'free_text') return 'Free-text';
  return q.allowMultiple ? 'Choice · multi' : 'Choice · single';
}

function answersPreview(q: DiscoveryQuestion): string {
  if (q.type === 'free_text') return '—';
  if (q.options.length <= 4) return q.options.map((o) => o.text).join(' · ');
  return `${q.options.length} options`;
}

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — see DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'text', order: 'ascend' },
};

/**
 * Questions Inventory is inherently per-company: the backend requires
 * `?companyId=` (422 without it). This console drives that companyId from the
 * global "Operate as tenant" scope (top-right `CompanyScopePicker`), which the
 * dataProvider auto-appends for the scope-aware `questions` resource.
 *
 *  - No tenant selected (`companyId === null`) → show a friendly prompt and DO
 *    NOT fire the list query (would 422). `queryOptions.enabled` gates it, and
 *    the DataTable is not rendered at all in this branch.
 *  - A tenant selected → the table loads that tenant's inventory. Switching the
 *    scope calls `queryClient.invalidateQueries()` (see CompanyScopeProvider),
 *    so the list refetches under the new companyId automatically.
 *
 * Row click opens the read-first detail page (`/questions/:id`); Edit is an
 * in-place toggle there, gated on `manage:DiscoveryQuestion`.
 */
export function QuestionList() {
  const navigate = useNavigate();
  const { companyId } = useCompanyScope();
  const hasScope = companyId != null;

  const { tableProps } = useTable<DiscoveryQuestion>({
    resource: 'questions',
    // Skip the query entirely when no tenant is scoped (avoids the 422).
    queryOptions: { enabled: hasScope },
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const { mutateAsync: deleteOne } = useDelete();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const rows = (tableProps.dataSource ?? []) as DiscoveryQuestion[];
  const defaultCount = rows.filter((r) => r.isDefault).length;

  const columns: DataColumn<DiscoveryQuestion>[] = [
    {
      dataIndex: 'text',
      key: 'text',
      title: 'Question',
      kind: 'text',
      width: 360,
      render: (t: string) => <b>{t}</b>,
    },
    {
      key: 'type',
      title: 'Type',
      kind: 'enum',
      width: 140,
      projectValue: (r) => typeLabel(r),
      render: (_: unknown, r: DiscoveryQuestion) => (
        <Tag color={r.type === 'free_text' ? 'purple' : 'blue'}>{typeLabel(r)}</Tag>
      ),
    },
    {
      key: 'answers',
      title: 'Answers',
      width: 220,
      filterable: false,
      // Escape hatch: sorts by option count rather than the rendered preview
      // text, which `kind`-derived comparators cannot express.
      sortable: (a, b) =>
        (a.type === 'free_text' ? 0 : a.options.length) -
        (b.type === 'free_text' ? 0 : b.options.length),
      render: (_: unknown, r: DiscoveryQuestion) => (
        <Typography.Text type="secondary">{answersPreview(r)}</Typography.Text>
      ),
    },
    {
      key: 'required',
      title: 'Required',
      kind: 'enum',
      width: 120,
      projectValue: (r) => (r.required ? 'Required' : 'Optional'),
      render: (_: unknown, r: DiscoveryQuestion) =>
        r.required ? (
          <Tag color="gold">Required</Tag>
        ) : (
          <Typography.Text type="secondary">Optional</Typography.Text>
        ),
    },
    {
      key: 'default',
      title: 'Default',
      kind: 'enum',
      width: 120,
      projectValue: (r) => (r.isDefault ? 'Default' : 'Not default'),
      render: (_: unknown, r: DiscoveryQuestion) =>
        r.isDefault ? (
          <Typography.Text type="warning">★ Default</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      title: 'Updated',
      kind: 'date',
      width: 140,
    },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: DiscoveryQuestion) => (
        <RowActionsMenu
          actions={[
            {
              key: 'delete',
              label: 'Delete',
              danger: true,
              cap: 'manage:DiscoveryQuestion',
              onRun: async () => {
                await deleteOne({
                  resource: 'questions',
                  id: record.id,
                  meta: { companyId },
                  // RowActionsMenu's confirm dialog owns the toast (via
                  // useConfirmAction) — suppress Refine's own notification
                  // provider so the operator does not see it twice.
                  successNotification: false,
                  errorNotification: false,
                });
              },
              confirm: {
                title: 'Delete this question?',
                okText: 'Delete',
                danger: true,
                successMessage: 'Question deleted',
              },
            },
          ]}
        />
      ),
    },
  ];

  if (!hasScope) {
    return (
      <List title="Questions Inventory" headerButtons={() => null}>
        <Empty
          description={selectTenantPrompt('manage its Questions Inventory')}
          style={{ padding: '48px 0' }}
        />
      </List>
    );
  }

  return (
    <List
      title="Questions Inventory"
      headerButtons={() => (
        <Can I="manage" a="DiscoveryQuestion">
          <Button type="primary" onClick={() => navigate('/questions/new')}>
            + New question
          </Button>
        </Can>
      )}
    >
      <Typography.Paragraph type="secondary">
        Reusable question templates. Flag questions as <b>Default</b> to auto-add them to new
        conferences. <Tag>{defaultCount} of {rows.length} default</Tag>
      </Typography.Paragraph>
      <DataTable<DiscoveryQuestion>
        tableProps={tableProps}
        columns={columns}
        rowKey="id"
        storageKey="questionsList"
        resetKey={companyId}
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['text']}
        searchPlaceholder="Search questions"
        onRowClick={(record) => navigate(`/questions/${record.id}`)}
        empty={{
          title: 'No questions yet',
          description: 'Add the first question to get started.',
        }}
      />
    </List>
  );
}
